import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useListDocuments } from "~/api/default/default";
import { getGetVolumesQueryKey, useGetVolumes } from "~/api/folders/folders";
import { DocumentStatus, FolderChatStatus } from "~/api/model";
import { useGetProject } from "~/api/projects/projects";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { ROUTES, useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useWebSocketCreateVolume } from "~/hooks/useWebSocketCreateVolume.ts";
import { cn } from "~/lib/utils";
import { VolumeCreationResponse } from "~/lib/wss-client.ts";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { useParentCase } from "../hooks/useParentCase.ts";
import { useDocumentSelectionStore } from "../stores/documentSelectionStore";

interface CreateVersionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentsToInclude?: string[]; // Optional - if undefined, use all docs
  defaultName?: string; // Optional - default version name
  customDescription?: string; // Optional - custom description text
  onSuccess?: () => void; // Optional - callback after creation
}

type VersionCreationState =
  | { status: "idle" }
  | { status: "creating"; versionName: string }
  | { status: "polling"; versionId: string; versionName: string }
  | { status: "success"; versionId: string }
  | { status: "error"; error: string };

const createVersionSchema = (volumeNames: string[] = []) =>
  z.object({
    versionName: z
      .string()
      .min(1, { message: "Version name is required" })
      .refine((name) => !volumeNames.includes(name.trim()), {
        message: "This version name already exists",
      }),
  });

type CreateVersionFormData = z.infer<ReturnType<typeof createVersionSchema>>;

export const CreateVersionModal: FC<CreateVersionDialogProps> = ({
  open,
  setOpen,
  documentsToInclude,
  defaultName,
  customDescription,
  onSuccess,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { project: parentCase } = useParentCase();
  const { selectedDocumentIds } = useDocumentSelectionStore();
  const { data: volumes } = useGetVolumes(organizationId, caseId);
  const { data: documentsList } = useListDocuments(organizationId, caseId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [creationState, setCreationState] = useState<VersionCreationState>({
    status: "idle",
  });

  const { data: pollingVersionData } = useGetProject(
    organizationId,
    creationState.status === "polling" ? creationState.versionId : "",
    {
      query: {
        enabled:
          creationState.status === "polling" && !!creationState.versionId,
        refetchInterval: (query) => {
          const data = query.state.data;
          if (!data) return 2000;
          return data.chatStatus === FolderChatStatus.PROCESSING ? false : 2000;
        },
      },
    }
  );

  // Handle version ready
  useEffect(() => {
    if (
      creationState.status === "polling" &&
      pollingVersionData?.chatStatus === FolderChatStatus.PROCESSING
    ) {
      setCreationState({
        status: "success",
        versionId: creationState.versionId,
      });

      toast({
        title: "Version ready",
        description: `Version "${creationState.versionName}" is ready. Redirecting...`,
      });

      setOpen(false);

      setTimeout(() => {
        navigate(
          ROUTES.CASE({
            organizationId,
            caseId: creationState.versionId,
          })
        );
      }, 500);
    }
  }, [creationState, pollingVersionData, navigate, organizationId, toast]);

  const versionNameDep =
    creationState.status === "creating" || creationState.status === "polling"
      ? creationState.versionName
      : undefined;

  // Handle WebSocket chat responses
  const handleVolumeCreationResponse = useCallback(
    (response: VolumeCreationResponse) => {
      if (response.action === "message-error") {
        setCreationState({
          status: "error",
          error:
            response.error || "Failed to create version. Please try again.",
        });
        toast({
          title: "Error",
          description:
            response.error || "Failed to create version. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (parentCase?.id) {
        queryClient.invalidateQueries({
          queryKey: getGetVolumesQueryKey(organizationId, parentCase.id),
        });
      }

      if (response.response?.id && creationState.status === "creating") {
        setCreationState({
          status: "polling",
          versionId: response.response.id,
          versionName: creationState.versionName,
        });
      }
    },
    [
      organizationId,
      parentCase?.id,
      queryClient,
      toast,
      creationState.status,
      versionNameDep,
    ]
  );

  // WebSocket create volume functionality
  const {
    createVolume: createWebSocketVolume,
    isConnected: isWebSocketConnected,
    isLoading: isWebSocketLoading,
  } = useWebSocketCreateVolume({
    onVolumeCreationResponse: handleVolumeCreationResponse,
  });

  const intl = useIntl();

  const volumesNames = volumes?.map((volume) => volume.name) || [];

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<CreateVersionFormData>({
    resolver: zodResolver(createVersionSchema(volumesNames)),
    defaultValues: {
      versionName: defaultName || "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!open) {
      reset({
        versionName: defaultName || "",
      });
      setCreationState({ status: "idle" });
    }
  }, [open, reset, defaultName]);

  const onSubmit = handleSubmit(async (data) => {
    trackEvent({
      eventName: "app/version_create_modal/create.button/submit",
    });

    if (!parentCase?.id) {
      return;
    }

    const completeDocumentIds =
      documentsToInclude !== undefined
        ? documentsList?.documents
            ?.filter((doc) => doc.status === DocumentStatus.COMPLETE)
            .map((doc) => doc.id) || []
        : selectedDocumentIds.filter((id) => {
            const document = documentsList?.documents?.find(
              (doc) => doc.id === id
            );
            return document?.status === DocumentStatus.COMPLETE;
          });

    if (!isWebSocketConnected) {
      setCreationState({
        status: "error",
        error: "Unable to create version. Please try again.",
      });
      toast({
        title: "Connection Error",
        description: "Unable to create version. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setCreationState({ status: "creating", versionName: data.versionName });

      createWebSocketVolume(
        parentCase.id,
        data.versionName,
        completeDocumentIds
      );

      onSuccess?.();
    } catch (error) {
      logger.error("WebSocket create volume failed", error);
      setCreationState({
        status: "error",
        error: "Failed to create version. Please try again.",
      });
      toast({
        title: "Error",
        description: "Failed to create version. Please try again.",
        variant: "destructive",
      });
    }
  });

  const isCreatingOrPolling =
    creationState.status === "creating" || creationState.status === "polling";

  const getChatStatusMessage = (chatStatus?: FolderChatStatus) => {
    if (!chatStatus) {
      return intl.formatMessage({
        id: "versionCreation.status.pleaseWait",
        defaultMessage: "Please wait...",
      });
    }

    switch (chatStatus) {
      case FolderChatStatus.DRAFT:
        return intl.formatMessage({
          id: "versionCreation.status.initializing",
          defaultMessage: "Initializing...",
        });
      case FolderChatStatus.COPYING:
        return intl.formatMessage({
          id: "versionCreation.status.copyingFiles",
          defaultMessage: "Copying files...",
        });
      case FolderChatStatus.PROCESSING:
        return intl.formatMessage({
          id: "versionCreation.status.buildingContext",
          defaultMessage: "Building chat context...",
        });
      case FolderChatStatus.UPDATING:
        return intl.formatMessage({
          id: "versionCreation.status.updating",
          defaultMessage: "Updating...",
        });
      case FolderChatStatus.COMPLETE:
        return intl.formatMessage({
          id: "versionCreation.status.ready",
          defaultMessage: "Ready",
        });
      case FolderChatStatus.ERROR:
        return intl.formatMessage({
          id: "versionCreation.status.error",
          defaultMessage: "An error occurred. Please try again.",
        });
      default:
        return intl.formatMessage({
          id: "versionCreation.status.pleaseWait",
          defaultMessage: "Please wait...",
        });
    }
  };

  const getModalTitle = () => {
    if (
      creationState.status === "creating" ||
      creationState.status === "polling"
    ) {
      return intl.formatMessage({
        id: "versionCreation.title.creating",
        defaultMessage: "Creating Version...",
      });
    }
    return intl.formatMessage({
      id: "createNewVersion",
      defaultMessage: "Create New Version",
    });
  };

  const getModalDescription = () => {
    if (creationState.status === "creating") {
      return (
        <FormattedMessage
          id="versionCreation.description.creating"
          defaultMessage='Version "<b>{versionName}</b>" is being created. You&apos;ll be redirected when ready.'
          values={{
            versionName: creationState.versionName,
            b: (chunks) => <strong className="font-semibold">{chunks}</strong>,
          }}
        />
      );
    }
    if (creationState.status === "polling") {
      return (
        <FormattedMessage
          id="versionCreation.description.preparing"
          defaultMessage='Version "<b>{versionName}</b>" is being prepared. You&apos;ll be redirected when ready.'
          values={{
            versionName: creationState.versionName,
            b: (chunks) => <strong className="font-semibold">{chunks}</strong>,
          }}
        />
      );
    }
    if (customDescription) {
      return customDescription;
    }
    return intl.formatMessage({
      id:
        documentsToInclude !== undefined
          ? "createVersionModal.descriptionAllDocuments"
          : "createVersionModal.descriptionSelectedDocuments",
      defaultMessage:
        documentsToInclude !== undefined
          ? "A new version will be created based on all documents. The files will be duplicated. All changes within the version will not affect the Master version."
          : "A new version will be created based on the selected documents. The files will be duplicated. All changes within the version will not affect the Master version.",
    });
  };

  return (
    <Modal
      open={open}
      closeDisabled={isCreatingOrPolling}
      onOpenChange={(newOpen) => {
        if (!isCreatingOrPolling) {
          setOpen(newOpen);
        }
      }}
      title={getModalTitle()}
      description={getModalDescription()}
      content={
        isCreatingOrPolling ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">
              {creationState.status === "polling"
                ? getChatStatusMessage(pollingVersionData?.chatStatus)
                : getChatStatusMessage(undefined)}
            </p>
          </div>
        ) : (
          <form
            id="create-version-form"
            onSubmit={onSubmit}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="version-name">
                <FormattedMessage
                  id="versionNameLabel"
                  defaultMessage="Version Name"
                />
              </Label>
              <Input
                id="version-name"
                autoFocus
                {...register("versionName")}
                placeholder={intl.formatMessage({
                  id: "versionNamePlaceholder",
                  defaultMessage: "New version name",
                })}
                className={cn(
                  "shadow-none mb-1",
                  errors.versionName ? "border-red-500" : ""
                )}
              />
              {errors.versionName && (
                <p className="text-red-500 text-sm">
                  {errors.versionName.message}
                </p>
              )}
            </div>
            {creationState.status === "error" && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800 text-sm">{creationState.error}</p>
              </div>
            )}
          </form>
        )
      }
      footer={
        isCreatingOrPolling ? null : (
          <>
            <Button
              variant="outline"
              type="button"
              disabled={isWebSocketLoading}
              onClick={() => {
                trackEvent({
                  eventName: "app/version_create_modal/cancel.button/click",
                });
                setOpen(false);
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              type="submit"
              disabled={isWebSocketLoading || !isValid}
              className="bg-blue-600 hover:bg-blue-700"
              form="create-version-form"
            >
              {isWebSocketLoading && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              <FormattedMessage
                id="createVersion"
                defaultMessage="Create Version"
              />
            </Button>
          </>
        )
      }
    />
  );
};
